<template>
    <div class="automation-wizard-panel-footer">
        <CompassAlert
            v-if="readOnly"
            type="neutral"
            :description="translations.noPermissionMessage"
            :showClose="false"
            :action="noPermissionMessageAction"
            actionPosition="bottom"
            class="automation-wizard-panel-footer__no-permission"
        />
        <CompassTooltip
            v-else-if="!hasFormSteps"
            :content="isEditMode ? translations.noConfigurationTooltip : ''"
            placement="left"
            :disabled="!isEditMode"
        >
            <CompassButton
                :label="noStepsButtonLabel"
                leftIcon="check"
                :loading="isLoading"
                :disabled="isEditMode"
                @click="$emit('activate-automation')"
            />
        </CompassTooltip>
        <template v-else>
            <CompassButton
                v-if="!isFirstStep"
                :label="translations.previous"
                type="tertiary"
                leftIcon="arrow-left"
                :disabled="isLoading"
                @click="$emit('previous-step')"
            />
            <CompassButton
                :label="nextButtonLabel"
                :leftIcon="isLastStep ? 'check' : ''"
                :loading="isLoading"
                @click="$emit('next-step')"
            />
        </template>
    </div>
</template>

<script>
import { handleCopyToClipboardWithToast } from 'Helpers/stringHelper';

const CONFIGURABLE_STEP_TYPES = ['trigger', 'action'];

export default {
    props: {
        allSteps: {
            type: Array,
            default: () => [],
        },
        currentStepIndex: {
            type: Number,
            default: 0,
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        isEditMode: {
            type: Boolean,
            default: false,
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        template: {
            type: Object,
            default: null,
        },
    },
    emits: ['activate-automation', 'previous-step', 'next-step'],
    data() {
        return {
            translations: {
                activateAutomation: this.$t.__(
                    'automation.AutomationWizardPanelFooter.activate_automation',
                ),
                editAutomation: this.$t.__(
                    'automation.AutomationWizardPanelFooter.edit_automation',
                ),
                saveChanges: this.$t.__('automation.AutomationWizardPanelFooter.save_changes'),
                previous: this.$t.__('automation.AutomationWizardPanelFooter.previous'),
                noConfigurationTooltip: this.$t.__(
                    'automation.AutomationWizardPanelFooter.no_configuration_tooltip',
                ),
                noPermissionMessage: this.$t.__(
                    'automation.AutomationWizardPanelFooter.no_permission_message',
                ),
            },
        };
    },
    computed: {
        noPermissionMessageAction() {
            return {
                label: this.$t.__('common.actions.copy_template_link'),
                leftIcon: 'duplicate',
                callback: () => {
                    const internalId = this.template?.metadata?.template?.internal_id;
                    if (internalId) {
                        const url = `${window.location.origin}/automations/templates/${internalId}`;
                        handleCopyToClipboardWithToast(url);
                    }
                },
            };
        },
        hasFormSteps() {
            return this.allSteps.some((step) => CONFIGURABLE_STEP_TYPES.includes(step.type));
        },
        isFirstStep() {
            return this.currentStepIndex === 0;
        },
        isLastStep() {
            return this.currentStepIndex === this.allSteps.length - 1;
        },
        remainingConfigSteps() {
            return this.allSteps
                .slice(this.currentStepIndex + 1)
                .filter((step) => CONFIGURABLE_STEP_TYPES.includes(step.type)).length;
        },
        noStepsButtonLabel() {
            return this.isEditMode
                ? this.translations.editAutomation
                : this.translations.activateAutomation;
        },
        nextButtonLabel() {
            if (this.isLastStep) {
                return this.isEditMode
                    ? this.translations.saveChanges
                    : this.translations.activateAutomation;
            } else {
                const translationKey = this.isEditMode
                    ? 'automation.AutomationWizardPanelFooter.next_edit_steps'
                    : 'automation.AutomationWizardPanelFooter.next_configure_steps';
                return this.$t.n__(
                    translationKey,
                    null,
                    this.remainingConfigSteps,
                    this.remainingConfigSteps,
                );
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.automation-wizard-panel-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: var(--spacing-3) var(--spacing-4);
    gap: var(--spacing-3);

    &__no-permission {
        width: 100%;
    }
}
</style>
